/**
 * Customized for:
 * 1. Adding recaptchaToken to login request
 * 2. Retrieve WCToken and WCTrustedToken from response body, and set in local storage for React site to use.
 * 3. Fire GTM event on login success or failure
 */

import { setDefaultRequest, useDefaultStore } from '@/data/Content/DefaultStore';
import { usePrivacyAndMarketing } from '@/data/Content/PrivacyAndMarketing';
import { getStoreLocale } from '@/data/Content/StoreLocale-Server';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getLocalization } from '@/data/Localization';
import { dFix, useSettings } from '@/data/Settings';
import { REMEMBERED_LOGON_ID } from '@/data/constants/cookie';
import { PASSWORD_EXPIRED } from '@/data/constants/errors';
import { NOT_APPLICABLE } from '@/data/constants/gtm';
import { CurrentUser, saveCurrentUserToStorage } from '@/data/forReactAndMobile/SaveCurrentUser';
import { WC_TOKEN, WC_TRUSTED_TOKEN } from '@/data/forReactAndMobile/constants';
import { useRememberMeState } from '@/data/state/useRememberMeState';
import { ID, TransactionErrorResponse } from '@/data/types/Basic';
import { ContentProps } from '@/data/types/ContentProps';
import { ErrorType } from '@/data/types/Error';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { isErrorType, processError } from '@/data/utils/processError';
import type {
	ComIbmCommerceRestMemberHandlerLoginIdentityHandlerLoginForm,
	ComIbmCommerceRestMemberHandlerLoginIdentityHandlerUserIdentity,
} from 'integration/generated/transactions/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import transactionsLoginIdentity from 'integration/generated/transactions/transactionsLoginIdentity';
import { GA4_AUTH_EVENTS } from 'presentation/events/constants/eventConstants';
import { useTrackAuthEvent } from 'presentation/events/hooks/useTrackAuthEvent';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { GA4LoginCompleteEventPayload } from 'presentation/events/types/ga4Types';
import { useState } from 'react';
import { MIN_EXP_TIME } from 'shared/custom/constants/commonConstants';
import { setCookie } from 'shared/custom/utils/cookieHandler';

export { personMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personMutatorKeyMatcher';
export type LoginResponse = ComIbmCommerceRestMemberHandlerLoginIdentityHandlerUserIdentity & {
	privacyNoticeVersion?: string;
	marketingTrackingConsent?: string;
	rememberMe?: boolean;
};

type LoginRequest = ComIbmCommerceRestMemberHandlerLoginIdentityHandlerLoginForm & {
	grecaptchaToken?: string;
};
export const loginFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		data: LoginRequest,
		params: RequestParams
	) =>
		(await transactionsLoginIdentity(pub).loginIdentityLogin(
			storeId,
			query,
			data,
			params
		)) as LoginResponse;

export type UserLogon = {
	logonId?: string;
	/**
	 * @deprecated do not use.
	 */
	email?: string;
	logonPassword: string;
	rememberMe: boolean;
	logonPasswordNew?: string;
	logonPasswordVerify?: string;
	resendToken?: boolean;
	verificationToken?: string;
	isSessionExpired?: string;
	isDialogView?: boolean;
};

export const getLogin = async ({ cache, context }: ContentProps) => {
	const { localeName: locale } = await getStoreLocale({ cache, context });
	await Promise.all([
		getLocalization(cache, locale, 'SignIn'),
		getLocalization(cache, locale, 'RegistrationB2BLayout'),
	]);
};

export const grecaptchaToken = async (
	GOOGLE_RECAPTCHA_SCORE_BASED_SITE_KEY: string
): Promise<string> => {
	const token = await (window as any).grecaptcha.enterprise.execute(
		GOOGLE_RECAPTCHA_SCORE_BASED_SITE_KEY,
		{ action: 'LOGIN' }
	);
	return token;
};

export const useLogin = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { langId } = getClientSideCommon(settings, router);
	const basename = process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '';
	const recaptchaScoreBasedKey = settings.userData.GOOGLE_RECAPTCHA_SCORE_BASED_SITE_KEY || '';
	const params = useExtraRequestParameters();
	const { physicalStoreId } = useDefaultStore();
	const {
		actions: { setRememberMe },
	} = useRememberMeState();
	const {
		privacyNoticeVersion: pnv,
		marketingTrackingConsent: mtc,
		setMarketingTrackingConsent,
		setPrivacyNoticeVersion,
	} = usePrivacyAndMarketing();
	const [passwordExpired, setPasswordExpired] = useState<(ErrorType & { user: UserLogon }) | null>(
		null
	);
	const trackAuthEvent = useTrackAuthEvent();
	const trackException = useTrackException();

	// eslint-disable-next-line complexity
	const loginSubmit = async (props: UserLogon) => {
		const { email = '', logonId: _logonId, rememberMe, ...rest } = props;
		const logonId: string = _logonId || email;

		const loginCompleteParams: GA4LoginCompleteEventPayload = {
			event: GA4_AUTH_EVENTS.LOGIN_COMPLETE,
			element_name: 'login form',
			element_type: props.isDialogView ? 'dialog' : 'page',
			page: window.location.href,
			element_location: props.isDialogView ? 'login dialog' : 'login page',
			status: 'success',
			error_type: NOT_APPLICABLE,
		};

		try {
			const resp = await loginFetcher(true)(
				settings?.storeId ?? '',
				{ rememberMe: props.rememberMe, updateCookies: 'true' },
				{
					logonId: logonId.toLowerCase(),
					grecaptchaToken: await grecaptchaToken(recaptchaScoreBasedKey),
					...rest,
				},
				params
			);

			await setDefaultRequest(
				{ defaultStore: physicalStoreId, fromPageName: '2' },
				settings?.storeId ?? '',
				langId,
				MIN_EXP_TIME,
				{
					headers: {
						[WC_TOKEN]: resp.WCToken,
						[WC_TRUSTED_TOKEN]: resp.WCTrustedToken,
					},
				}
			);

			// the response of loginIdentity has privacyNoticeVersion and marketingTrackingConsent if set for the user.
			const { privacyNoticeVersion, marketingTrackingConsent } = resp;
			// if privacyNoticeVersion exists in session, means it is enabled. Set it from user context
			pnv && setPrivacyNoticeVersion(privacyNoticeVersion ? dFix(privacyNoticeVersion) : undefined);
			mtc &&
				setMarketingTrackingConsent(
					marketingTrackingConsent ? dFix(marketingTrackingConsent) : undefined
				);
			setRememberMe(props.rememberMe);

			const currentUser = {
				...resp,
				userLoggedIn: true,
				isGuest: false,
				generatedCookie: true,
			} as CurrentUser;

			saveCurrentUserToStorage(basename, currentUser);

			if (logonId) {
				setCookie(REMEMBERED_LOGON_ID, logonId, 30);
			}

			trackAuthEvent(loginCompleteParams);

			return resp;
		} catch (e) {
			const error: TransactionErrorResponse | ErrorType = processError(
				e as TransactionErrorResponse
			);

			let errorCode = 'UNEXPECTED_ERROR';
			if (isErrorType(error)) {
				errorCode = error.error.errorKey || errorCode;
			}

			trackAuthEvent({
				...loginCompleteParams,
				status: 'error',
				error_type: errorCode,
			});

			trackException(e, props.isDialogView);

			if (isErrorType(error) && error.error.errorKey === PASSWORD_EXPIRED) {
				setPasswordExpired({ ...error, user: { ...props } });
			}
			return error;
		}
	};

	return {
		loginSubmit,
		passwordExpired,
		setPasswordExpired,
	};
};
